import { useState, useEffect, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import styles from './Auth.module.css'

/**
 * Pagina "Password dimenticata" — chiede l'email e attiva il flusso
 * di reset Supabase. L'utente riceve un link via email che lo porta
 * alla pagina ResetPassword.
 *
 * Per sicurezza l'endpoint backend risponde sempre con successo, anche
 * se l'email non è registrata (no account enumeration).
 */
export function PasswordDimenticata() {
  const [email, setEmail] = useState('')
  const [stato, setStato] = useState<'form' | 'inviato' | 'errore'>('form')
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)
  // Countdown del bottone "Reinvia" per evitare spam di email
  const [secondiAttesa, setSecondiAttesa] = useState(0)

  // Countdown reinvio
  useEffect(() => {
    if (secondiAttesa <= 0) return
    const id = setTimeout(() => setSecondiAttesa((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [secondiAttesa])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setCaricamento(true)
    setErrore(null)
    try {
      await api.post('/auth/password-dimenticata', { email })
      setStato('inviato')
      setSecondiAttesa(60)
    } catch (err) {
      setErrore(messaggioErrore(err))
      setStato('errore')
    } finally {
      setCaricamento(false)
    }
  }

  const reinvia = async () => {
    if (secondiAttesa > 0) return
    setCaricamento(true)
    try {
      await api.post('/auth/password-dimenticata', { email })
      setSecondiAttesa(60)
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logoBox}>RC</div>
          <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
        </div>

        {stato === 'inviato' ? (
          // ─── Conferma invio ─────────────────────────────────────
          <>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'var(--info-soft)', color: 'var(--info)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Mail size={28} />
            </div>
            <h1 className={styles.titolo} style={{ textAlign: 'center' }}>
              Controlla la tua email
            </h1>
            <p className={styles.descrizione} style={{ textAlign: 'center' }}>
              Se l'indirizzo <strong>{email}</strong> è registrato,
              riceverai le istruzioni a breve.
            </p>
            <div className={styles.success}>
              Controlla anche la cartella <strong>spam</strong> se non vedi
              l'email entro qualche minuto. Il link è valido <strong>1 ora</strong>.
            </div>

            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Button
                variante="secondary"
                taglia="lg"
                onClick={reinvia}
                caricamento={caricamento}
                disabled={secondiAttesa > 0}
              >
                {secondiAttesa > 0
                  ? `Reinvia tra ${secondiAttesa}s`
                  : 'Non ho ricevuto l\'email — Reinvia'}
              </Button>
            </div>
          </>
        ) : (
          // ─── Form ──────────────────────────────────────────────
          <>
            <h1 className={styles.titolo}>Password dimenticata</h1>
            <p className={styles.descrizione}>
              Inserisci la tua email e ti mandiamo le istruzioni per
              reimpostare la password.
            </p>

            <form onSubmit={submit} className={styles.form}>
              <label className={styles.campo}>
                <span className={styles.lbl}>Email</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  placeholder="mario@esempio.it"
                />
              </label>

              {errore && <div className={styles.errore}>{errore}</div>}

              <Button type="submit" taglia="lg" caricamento={caricamento}>
                Invia istruzioni
              </Button>
            </form>
          </>
        )}

        <div className={styles.linkRiga} style={{ marginTop: 24 }}>
          <Link to="/accedi" className={styles.link}>
            <ArrowLeft size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  )
}
